import { CodeConfigurationField } from '@dtx-company/inter-app/src/types/flowcode'
import { FLAGS_BY_REGION, RegionCode } from '@dtx-company/inter-app/src/constants/flagsByRegion'
import { Grid, Select, Stack, TextArea, TextField } from '@dtx-company/design-system/src'
import { MESSAGE_MAX_LENGTH } from '../constants'
import { PhoneOutlined } from '@mui/icons-material'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { getCountryCodeForRegionCode, getSupportedRegionCodes } from 'awesome-phonenumber'
import {
  selectBody,
  selectError,
  selectNormalizedScanDestination,
  selectRegionCode
} from '@app/code/src/machines/locg/locgSelectors'
import { useGlobalServices } from '@app/common/src/hooks/useGlobalServices'
import { useOnChange } from '../hooks/useOnChange'
import { useSelector } from '@xstate/react'
import React, { FC, useMemo } from 'react'
import useTranslation from 'next-translate/useTranslation'

export const SMSField: FC = () => {
  const { t } = useTranslation('logged-out-generator')
  const { locgService } = useGlobalServices()
  const scanDestination = useSelector(locgService, selectNormalizedScanDestination)
  const regionCode = useSelector(locgService, selectRegionCode)
  const body = useSelector(locgService, selectBody)
  const recipientError = useSelector(
    locgService,
    selectError
  )(CodeConfigurationField.SCAN_DESTINATION)
  const messageError = useSelector(locgService, selectError)(CodeConfigurationField.BODY)

  const regionSelectOptions = useMemo(() => {
    return (getSupportedRegionCodes() as RegionCode[]).map(regionCode => ({
      label: `${regionCode} ${FLAGS_BY_REGION[regionCode]} (+${getCountryCodeForRegionCode(
        regionCode
      )})`,
      value: regionCode
    }))
  }, [])

  const onChange = useOnChange()

  return (
    <Stack gap={{ xs: 2, md: 4 }}>
      <Grid
        container
        spacing={2}
        sx={{
          '.MuiFormControl-root': {
            minWidth: 'unset'
          }
        }}
      >
        <Grid xs={5} md={4}>
          <Select
            size="large"
            name={CodeConfigurationField.REGION_CODE}
            label={t(
              'LoggedOutGenerator.ScanDestination.options.sms.subcomponents.RegionSelector.label'
            )}
            hiddenLabel
            value={regionCode}
            options={regionSelectOptions}
            onChange={onChange}
            onFocus={() =>
              fireAnalyticsEvent('LoggedOutGenerator_ScanDestinationInputSMSRegion_Focused')
            }
            data-heap-id="LoggedOutGenerator_ScanDestinationInputSMSRegionSelector"
          />
        </Grid>
        <Grid xs={7} md={8}>
          <TextField
            size="large"
            name={CodeConfigurationField.SCAN_DESTINATION}
            label={t(
              'LoggedOutGenerator.ScanDestination.options.sms.subcomponents.PhoneNumberInput.label'
            )}
            hiddenLabel
            value={scanDestination}
            placeholder={t(
              'LoggedOutGenerator.ScanDestination.options.sms.subcomponents.PhoneNumberInput.placeholder'
            )}
            type="tel"
            startAdornment={<PhoneOutlined sx={{ color: 'onSurface.secondary' }} />}
            onChange={onChange}
            onFocus={() =>
              fireAnalyticsEvent('LoggedOutGenerator_ScanDestinationInputSMSRecipient_Focused')
            }
            onBlur={e => {
              fireAnalyticsEvent('LoggedOutGenerator_ScanDestinationInputSMSRecipient_Blurred', {
                text: e.target.value
              })
            }}
            inputProps={{
              'data-heap-id': 'LoggedOutGenerator_ScanDestinationInputSMSRecipient'
            }}
            fullWidth
            error={!!recipientError}
            helperText={recipientError}
          />
        </Grid>
      </Grid>

      <TextArea
        name={CodeConfigurationField.BODY}
        label={t('LoggedOutGenerator.ScanDestination.options.sms.subcomponents.MessageInput.label')}
        hiddenLabel
        value={body}
        placeholder={t(
          'LoggedOutGenerator.ScanDestination.options.sms.subcomponents.MessageInput.placeholder'
        )}
        onChange={onChange}
        onFocus={() =>
          fireAnalyticsEvent('LoggedOutGenerator_ScanDestinationInputSMSMessage_Focused')
        }
        onBlur={e => {
          fireAnalyticsEvent('LoggedOutGenerator_ScanDestinationInputSMSMessage_Blurred', {
            text: e.target.value
          })
        }}
        helperText={t(
          'LoggedOutGenerator.ScanDestination.options.sms.subcomponents.MessageInput.helperText',
          { characterCount: body?.length ?? 0, characterLimit: MESSAGE_MAX_LENGTH }
        )}
        inputProps={{
          'data-heap-id': 'LoggedOutGenerator_ScanDestinationInputSMSMessage'
        }}
        error={!!messageError}
      />
    </Stack>
  )
}
